import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, RotateCcw, X, Clock, Target } from 'lucide-react';
import { useWorkflow } from '../../context/WorkflowContext';

const StageContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2rem 0;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const ContentPanel = styled(motion.div)`
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  position: relative;
  z-index: 10;
  margin: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 90vh;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.5);
  }
  
  @media (max-width: 1024px) {
    max-width: 90%;
    padding: 2.5rem;
    margin: 1.5rem;
  }
  
  @media (max-width: 768px) {
    max-width: 95%;
    padding: 2rem;
    margin: 1rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 1.5rem;
    margin: 0.5rem;
    border-radius: 10px;
  }
`;

const SuccessIcon = styled(motion.div)`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 10px 30px rgba(74, 222, 128, 0.3);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const MetricsContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  margin: 2rem 0;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const MetricsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const MetricCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const MetricIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const Button = styled(motion.button)`
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  &.primary {
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
  }

  &.secondary {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
  }

  &.quit {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CompletedStage = () => {
  const navigate = useNavigate();
  const { sessionId, completedStages } = useWorkflow();

  // Calculate completion metrics
  const totalStages = 3; // story, dialogues, questions
  const completedCount = completedStages.size;
  const completionPercentage = Math.round((completedCount / totalStages) * 100);
  
  // Get current time for session duration (simplified)
  const sessionStartTime = localStorage.getItem('workflowStartTime');
  const sessionDuration = sessionStartTime 
    ? Math.round((Date.now() - parseInt(sessionStartTime)) / 1000 / 60) // minutes
    : 0;

  const handleStartNew = () => {
    // Clear session data
    localStorage.removeItem('workflowSessionId');
    localStorage.removeItem('workflowStartTime');
    navigate('/');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleQuit = () => {
    // Clear session data
    localStorage.removeItem('workflowSessionId');
    localStorage.removeItem('workflowStartTime');
    // Close the application (in a real app, this might close the window)
    window.close();
  };

  return (
    <StageContainer>
      <ContentPanel
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <SuccessIcon
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <CheckCircle size={50} color="white" />
        </SuccessIcon>

        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Congratulations!
        </Title>

        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          You have successfully completed the educational storytelling workflow. 
          All stages have been approved and your interactive learning experience is ready!
        </Subtitle>

        <MetricsContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <MetricsTitle>Completion Metrics</MetricsTitle>
          <MetricsGrid>
            <MetricCard
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <MetricIcon>
                <Target size={24} color="white" />
              </MetricIcon>
              <MetricValue>{completionPercentage}%</MetricValue>
              <MetricLabel>Completion Rate</MetricLabel>
            </MetricCard>

            <MetricCard
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              <MetricIcon>
                <CheckCircle size={24} color="white" />
              </MetricIcon>
              <MetricValue>{completedCount}/{totalStages}</MetricValue>
              <MetricLabel>Stages Completed</MetricLabel>
            </MetricCard>

            <MetricCard
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.0 }}
            >
              <MetricIcon>
                <Clock size={24} color="white" />
              </MetricIcon>
              <MetricValue>{sessionDuration}m</MetricValue>
              <MetricLabel>Session Duration</MetricLabel>
            </MetricCard>
          </MetricsGrid>
        </MetricsContainer>

        <ActionButtons
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
        >
          <Button
            className="primary"
            onClick={handleStartNew}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <RotateCcw size={20} />
            Start New Workflow
          </Button>
          
          <Button
            className="secondary"
            onClick={handleGoHome}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Home size={20} />
            Go to Home
          </Button>

          <Button
            className="quit"
            onClick={handleQuit}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <X size={20} />
            Quit
          </Button>
        </ActionButtons>
      </ContentPanel>
    </StageContainer>
  );
};

export default CompletedStage;